<template>
  <v-dialog :value="value" @input="$emit('input', $event)" :retain-focus="false" persistent
    max-width="480px" content-class="topup-dialog-wrap">
    <v-card class="topup-dialog">
      <div class="td-head">
        <v-icon class="td-head__icon">mdi-wallet-plus-outline</v-icon>
        <span class="td-head__title">{{ $t("other.add_balance") }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-card-text class="td-body">

        <!-- ─────────── RUB / Tinkoff ─────────── -->
        <template v-if="branch === 'tinkoff'">
          <!-- FT-147: способ рублёвой оплаты — картой (эквайринг) или по счёту (T-Bank invoicing) -->
          <v-radio-group v-model="rubPaymentType" row dense hide-details class="mt-0 mb-2 d-flex justify-center">
            <v-radio :label="$t('transfers.tbank_pay_by_card')" value="card"></v-radio>
            <v-radio :label="$t('transfers.tbank_pay_by_invoice')" value="invoice"></v-radio>
          </v-radio-group>

          <div v-if="rubPaymentType === 'card' || !tbankInvoice" class="td-amount">
            <v-text-field v-model.trim="sum" @focus="onSumFocus" hide-details="auto"
              :rules="sumRules" :suffix="planCurrency" :label="$t('other.sum')" class="td-amount__field"></v-text-field>
            <!-- FT-150 REQ-03 / DEC-02: минимум и его логика — под полем, в RUB-ветке тоже. -->
            <div class="td-foot" v-if="minTopupSum"><p class="mb-0">{{ topupHint }}</p></div>
          </div>

          <template v-if="rubPaymentType === 'card'">
            <v-text-field v-model.trim="promo_code" @click.once="promo_code = null" hide-details="auto"
              :label="$t('other.promo_code')" class="td-promo"></v-text-field>

            <div class="td-autopay" :class="{ 'td-autopay--on': autopayConsent }">
              <v-expansion-panels flat tile class="td-autopay__panel">
                <v-expansion-panel>
                  <v-expansion-panel-header class="td-autopay__header">
                    <div class="d-flex align-center">
                      <v-icon small class="td-autopay__icon" :color="autopayConsent ? 'success' : ''">mdi-autorenew</v-icon>
                      <span class="td-autopay__title">{{ $t("transfers.start_recurrent") }}</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="td-autopay__content">
                    <div class="td-notice">
                      <v-icon small class="td-notice__icon">mdi-information-outline</v-icon>
                      <span>{{ $t("transfers.autopay_with_topup_hint") }}</span>
                    </div>
                    <div class="td-autopay__fields">
                      <v-text-field v-model.trim="rebill_limit" type="number" dense outlined hide-details="auto"
                        :rules="rebillLimitRules" :suffix="planCurrency" :label="$t('transfers.rebill_limit_short')"></v-text-field>
                      <v-text-field v-model.trim="rebill_sum" type="number" dense outlined hide-details="auto"
                        :rules="rebillSumRules" :suffix="planCurrency" :label="$t('transfers.rebill_sum_title')"></v-text-field>
                    </div>
                    <div class="td-links">
                      <a :href="rebillDocUrl('tariffs')" target="_blank">{{ $t("transfers.link_tariffs") }}</a>
                      <span class="td-links__sep">·</span>
                      <a :href="rebillDocUrl('agreement')" target="_blank">{{ $t("transfers.link_agreement") }}</a>
                      <span class="td-links__sep">·</span>
                      <a :href="rebillDocUrl('privacy')" target="_blank">{{ $t("transfers.link_privacy") }}</a>
                    </div>
                    <v-checkbox v-model="autopayConsent" hide-details dense class="td-consent"
                      :label="$t('transfers.rebill_consent')"></v-checkbox>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <v-btn block large color="success" class="td-pay"
              :disabled="!sumValid || (autopayConsent && !autopayReady) || isLoading" :loading="isLoading"
              @click="transactionCreate()">
              <v-icon small class="mr-2">{{ autopayConsent ? 'mdi-autorenew' : 'mdi-arrow-right' }}</v-icon>
              {{ autopayConsent ? $t("transfers.pay_and_start_recurrent") : $t("other.pay") }}
            </v-btn>
          </template>

          <!-- ─────────── RUB / счёт T-Bank (FT-147 REQ-01, REQ-02) ─────────── -->
          <template v-else-if="!tbankInvoice">
            <p class="subtitle-2 mt-4 mb-2">{{ $t("transfers.tbank_payer_title") }}</p>
            <v-row dense>
              <v-col cols="12">
                <v-text-field v-model.trim="tbankPayer.name" :label="$t('transfers.tbank_payer_name') + ' *'" dense outlined hide-details="auto"
                  :rules="[v => !!v || $t('errors.required_field')]"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.trim="tbankPayer.inn" :label="$t('transfers.tbank_payer_inn') + ' *'" dense outlined hide-details="auto"
                  inputmode="numeric" :rules="tbankInnRules"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.trim="tbankPayer.kpp" :label="$t('transfers.tbank_payer_kpp')" dense outlined hide-details="auto"
                  inputmode="numeric" :disabled="tbankPayerIsSoleProprietor" :rules="tbankKppRules"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model.trim="tbankPayer.email" :label="$t('transfers.tbank_payer_email') + ' *'" dense outlined hide-details="auto"
                  type="email" :rules="tbankEmailRules"></v-text-field>
              </v-col>
            </v-row>
            <div class="td-notice mt-2">
              <v-icon small class="td-notice__icon">mdi-information-outline</v-icon>
              <span>{{ $t("transfers.tbank_invoice_hint") }}</span>
            </div>
            <v-btn block large color="primary" class="td-pay" :disabled="!sumValid || isLoading" :loading="isLoading" @click="tbankCreateInvoice()">
              <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
              {{ $t("transfers.tbank_generate_invoice") }}
            </v-btn>
          </template>

          <template v-else>
            <div class="td-tbank-result text-center">
              <p class="subtitle-1 mb-1">{{ $t("transfers.tbank_invoice_issued", { number: tbankInvoice.invoice_number || "", sum: tbankInvoice.sum }) }}</p>
              <p v-if="tbankDueDateLabel" class="text-caption grey--text mb-3">{{ $t("transfers.tbank_due_date") }}: {{ tbankDueDateLabel }}</p>
              <img v-if="tbankQrDataUrl" :src="tbankQrDataUrl" :alt="$t('transfers.tbank_qr_hint')" class="td-qr" />
              <p v-if="tbankQrDataUrl" class="text-caption mt-2 mb-3">{{ $t("transfers.tbank_qr_hint") }}</p>
              <v-alert v-else-if="tbankQrFailed" type="warning" dense text class="mt-2 mb-3 text-left">{{ $t("transfers.tbank_qr_unavailable") }}</v-alert>
              <v-alert type="info" dense text class="mt-1 mb-3 text-left">{{ $t("transfers.tbank_do_not_change") }}</v-alert>
              <v-btn v-if="tbankInvoice.pdf_url" block large color="primary" class="td-pay" :href="tbankInvoice.pdf_url" target="_blank" rel="noopener noreferrer">
                <v-icon small class="mr-2">mdi-file-pdf-box</v-icon>
                {{ $t("transfers.tbank_download_invoice") }}
              </v-btn>
              <v-btn text small class="mt-2" @click="tbankResetInvoice()">{{ $t("transfers.tbank_new_invoice") }}</v-btn>
            </div>
          </template>
        </template>

        <!-- ─────────── Paddle ─────────── -->
        <template v-else-if="branch === 'paddle'">
          <v-alert v-if="paddleUnavailableOnRu" type="warning" dense text class="mt-1 td-info-alert">
            {{ $t("transfers.paddle_only_on_com") }} <a href="https://web.rentprog.com" target="_blank">web.rentprog.com</a>
          </v-alert>
          <v-radio-group v-model="paddlePaymentType" row dense hide-details class="mt-0 mb-2 d-flex justify-center">
            <v-radio :label="$t('tariffs.pay_now')" value="transaction"></v-radio>
            <v-radio :label="$t('tariffs.pay_by_invoice')" value="invoice"></v-radio>
          </v-radio-group>

          <div class="td-amount">
            <v-text-field v-model.trim="sum" @focus="onSumFocus" hide-details="auto"
              :rules="sumRules" :suffix="planCurrency" :label="$t('other.sum')" class="td-amount__field"></v-text-field>
          </div>

          <template v-if="paddlePaymentType === 'invoice'">
            <v-menu v-model="showDueDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formattedInvoiceDueDate" :label="$t('tariffs.invoice_due_date')"
                  prepend-icon="mdi-calendar" readonly hide-details="auto" class="mt-3" v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="invoiceDueDate" :min="minInvoiceDueDate" :max="maxInvoiceDueDate" @input="showDueDateMenu = false"></v-date-picker>
            </v-menu>

            <p class="subtitle-2 mt-4 mb-2">{{ $t("tariffs.billing_address") }}</p>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-autocomplete v-model="billingAddress.country_code" :items="sortedCountries" item-text="text" item-value="value"
                  :label="$t('custom.country') + ' *'" dense outlined :rules="[v => !!v || $t('errors.required_field')]"></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="billingAddress.city" :label="$t('custom.city') + ' *'" dense outlined
                  :rules="[v => !!v || $t('errors.required_field')]"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="billingAddress.first_line" :label="$t('custom.address') + ' *'" dense outlined
                  :rules="[v => !!v || $t('errors.required_field')]"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="billingAddress.postal_code" :label="$t('custom.zip') + ' *'" dense outlined
                  :rules="[v => !!v || $t('errors.required_field')]"></v-text-field>
              </v-col>
            </v-row>
          </template>

          <v-btn v-if="paddlePaymentType === 'transaction'" block large color="success" class="td-pay"
            :disabled="!sumValid || paddleUnavailableOnRu || isLoading" :loading="isLoading" @click="paddleCreateTransaction()">
            {{ $t("other.pay") }}
          </v-btn>
          <v-btn v-else block large color="primary" class="td-pay"
            :disabled="!sumValid || isLoading" :loading="isLoading" @click="paddleCreateInvoice()">
            {{ $t("tariffs.generate_invoice") }}
          </v-btn>

          <div class="td-foot" v-if="minTopupSum">
            <p class="mb-1">{{ topupHint }}</p>
            <p class="mb-1">{{ $t("other.vat_not_included") }}</p>
            <p class="mb-1">* {{ $t("tariffs.inn_optional_info") }}</p>
          </div>
          <div class="td-foot" v-if="paddlePaymentType === 'invoice'">
            <p class="mb-0"><v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t("transfers.invoice_info") }}</p>
          </div>
        </template>

        <!-- ─────────── Euro (Stripe Invoice для юрлиц / Paddle для физлиц) ─────────── -->
        <template v-else>
          <div class="td-amount">
            <v-text-field v-model.trim="euroPaymentSum" @focus="onEuroSumFocus" type="number" hide-details="auto"
              :rules="sumRules" :suffix="planCurrency || 'EUR'" :label="$t('other.sum')" class="td-amount__field"></v-text-field>
          </div>

          <v-alert v-if="!isValidatedCompany" type="info" dense text class="mt-3 td-info-alert">
            {{ $t("tariffs.vat_info_message") }}
          </v-alert>

          <v-row dense align="center" class="mt-4">
            <v-col cols="4" sm="3">
              <v-autocomplete v-model="vatCountryCode" :items="countriesWithFlags" item-text="label" item-value="code"
                :label="$t('custom.country')" dense outlined hide-details @change="onVatCountryChange">
                <template v-slot:selection="{ item }">
                  <span class="mr-1">{{ item.flag }}</span><span>{{ item.code }}</span>
                </template>
                <template v-slot:item="{ item }">
                  <span class="mr-2">{{ item.flag }}</span><span>{{ item.label }}</span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="8" sm="9" class="d-flex align-center">
              <v-text-field v-model.trim="stripeVatNumber" :label="$t('tariffs.company_vat_optional')" @input="onVatInput"
                :placeholder="vatPlaceholder" dense outlined hide-details clearable class="flex-grow-1"></v-text-field>
              <v-icon v-if="vatValidationLoading" class="rotating ml-2" small>mdi-loading</v-icon>
              <v-icon v-else-if="isValidatedCompany" color="success" class="ml-2" small>mdi-check-circle</v-icon>
            </v-col>
          </v-row>

          <template v-if="isValidatedCompany">
            <p class="subtitle-2 mt-4 mb-2">{{ $t("tariffs.billing_address") }}</p>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field v-model="stripeBillingAddress.city" :label="$t('custom.city') + ' *'" dense outlined
                  :rules="[v => !!v || $t('errors.required_field')]"></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field v-model="stripeBillingAddress.first_line" :label="$t('custom.address') + ' *'" dense outlined
                  :rules="[v => !!v || $t('errors.required_field')]"></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="stripeBillingAddress.postal_code" :label="$t('custom.zip') + ' *'" dense outlined
                  :rules="[v => !!v || $t('errors.required_field')]"></v-text-field>
              </v-col>
            </v-row>

            <v-menu v-model="showStripeDueDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formattedStripeInvoiceDueDate" :label="$t('tariffs.invoice_due_date')"
                  readonly hide-details="auto" class="mt-3" v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="stripeInvoiceDueDate" :min="minInvoiceDueDate" :max="maxInvoiceDueDate" @input="showStripeDueDateMenu = false"></v-date-picker>
            </v-menu>

            <!-- disabled обязан включать isLoading: :loading у VBtn 2.7.2 ставит только
                 pointer-events:none (мышь), но кнопка остаётся в фокусе и активируется
                 с клавиатуры — второй Enter уходил вторым запросом на создание счёта. -->
            <v-btn block large color="primary" class="td-pay" @click="stripeCreateInvoice()"
              :disabled="!euroSumValid || vatValidationLoading || isLoading" :loading="isLoading">
              <v-icon left small>mdi-file-document-outline</v-icon>
              {{ $t("tariffs.generate_invoice") }}
            </v-btn>
            <div class="td-foot"><p class="mb-0">{{ $t("transfers.b2b_invoice_info") }}</p></div>
          </template>

          <template v-else>
            <v-alert v-if="paddleUnavailableOnRu" type="warning" dense text class="mt-2 td-info-alert">
              {{ $t("transfers.paddle_only_on_com") }} <a href="https://web.rentprog.com" target="_blank">web.rentprog.com</a>
            </v-alert>
            <v-btn block large color="success" class="td-pay" :disabled="!euroSumValid || paddleUnavailableOnRu || isLoading"
              @click="paddlePayWithEuro()" :loading="isLoading">
              <v-icon left small>mdi-credit-card</v-icon>
              {{ $t("other.pay") }}
            </v-btn>
            <div class="td-foot"><p class="mb-0">{{ $t("tariffs.vat_will_be_added") }}</p></div>
          </template>

          <div class="td-foot">
            <p class="mb-1">{{ $t("other.sum_warn_euro") }}</p>
            <p class="mb-0" v-if="minTopupSum">{{ topupHint }}</p>
          </div>

          <!-- FT-071: Stripe-автоплатёж (hosted Checkout) — рекуррентная привязка карты, отдельная
               секция под формой разового пополнения. Привязка прилетает вебхуком; FE по возвращении
               просто обновляет main_company. Никакого Stripe.js на FE.
               Гейт под Stripe-путь: «Подключить» предлагаем только валидированным B2B (у физлиц
               разовый платёж идёт через Paddle — не смешиваем провайдеров). Уже подключённого
               клиента не прячем — чтобы он мог отвязать. -->
          <div class="td-autopay-section" v-if="isValidatedCompany || isStripeAutopayConnected">
            <div class="td-divider"></div>
            <div class="td-autopay td-autopay--card" :class="{ 'td-autopay--on': isStripeAutopayConnected }">
              <div class="td-autopay__head">
                <v-icon small class="td-autopay__icon" :color="isStripeAutopayConnected ? 'success' : ''">mdi-autorenew</v-icon>
                <div class="td-autopay__body">
                  <div class="td-autopay__title">{{ $t("transfers.stripe_autopay_title") }}</div>
                  <template v-if="isStripeAutopayConnected">
                    <div v-if="stripeCardLabel" class="td-autopay__card">
                      <v-icon x-small class="td-autopay__card-icon">mdi-credit-card-outline</v-icon>{{ stripeCardLabel }}
                    </div>
                    <div class="td-autopay__sub">{{ $t("transfers.stripe_autopay_connected") }}</div>
                  </template>
                  <div class="td-autopay__sub" v-else>{{ $t("transfers.stripe_autopay_hint") }}</div>
                </div>
                <v-icon v-if="isStripeAutopayConnected" small color="success" class="td-autopay__check">mdi-check-circle</v-icon>
              </div>
              <v-btn v-if="!isStripeAutopayConnected" block small color="success" outlined
                class="td-autopay__btn td-autopay__btn--connect"
                :loading="stripeAutopayLoading" @click="connectStripeAutopay()">
                <v-icon left small>mdi-credit-card-plus-outline</v-icon>
                {{ $t("transfers.stripe_autopay_connect") }}
              </v-btn>

              <div class="td-autopay__settings">
                <div class="td-autopay__fields">
                  <v-text-field v-model.trim="stripeRebillLimit" type="number" dense outlined hide-details="auto"
                    :rules="rebillLimitRules" :suffix="planCurrency || 'EUR'" :label="$t('transfers.rebill_limit_short')"></v-text-field>
                  <v-text-field v-model.trim="stripeRebillSum" type="number" dense outlined hide-details="auto"
                    :rules="rebillSumRules" :suffix="planCurrency || 'EUR'" :label="$t('transfers.rebill_sum_title')"></v-text-field>
                </div>
                <div v-if="isStripeAutopayConnected" class="td-autopay__footer">
                  <v-btn small text color="primary" class="td-autopay__btn"
                    :loading="stripeAutopaySaving" :disabled="!autopaySettingsValid" @click="saveStripeAutopaySettings()">
                    <v-icon left x-small>mdi-content-save-outline</v-icon>
                    {{ $t("custom.save") }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn small text color="error" class="td-autopay__btn"
                    :loading="stripeAutopayLoading" @click="detachStripeAutopay()">
                    <v-icon left x-small>mdi-credit-card-remove-outline</v-icon>
                    {{ $t("transfers.stripe_autopay_detach") }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </template>

      </v-card-text>
      <v-card-actions class="td-actions">
        <v-spacer></v-spacer>
        <v-btn text class="td-close" @click="close">{{ $t("custom.close") }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { initializePaddle } from "@paddle/paddle-js";
import { countries } from "./countries";
import { isValidInn, isValidKpp, isValidEmail, isSoleProprietor, defaultRequisites, requisitesErrorKey, requisitesPayload } from "@/utils/tbankInvoiceQr";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import moment from "moment";

// Единый диалог пополнения баланса (RUB Tinkoff + промокод + инлайн-автоплатёж;
// Paddle оплата/счёт; EUR VAT-гейт → Stripe Invoice (юрлицо) / Paddle (физлицо)).
// Используется и в App.vue (хедер/paywall), и в Transactions.vue (страница) — единый
// источник истины. Контекст берёт из Vuex; видимость — v-model; @paid → родитель
// перезагружает таблицу транзакций (page-хвосты развязаны).
let ptxnHandled = false; // module-level: _ptxn-возврат Paddle обрабатываем один раз глобально
let stripeAutopayReturnHandled = false; // FT-071: ?stripe_autopay=... возврат из hosted Checkout — один раз глобально

export default {
  name: "TopupDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      sum: 0,
      promo_code: null,
      euroPaymentSum: 0,
      paddlePaymentType: "transaction",
      invoiceDueDate: null,
      showDueDateMenu: false,
      billingAddress: { country_code: null, city: "", first_line: "", postal_code: "" },
      vatCountryCode: null,
      stripeVatNumber: "",
      vatValidationLoading: false,
      vatValidationResult: null,
      vatValidationDebounce: null,
      stripeInvoiceDueDate: null,
      showStripeDueDateMenu: false,
      stripeBillingAddress: { country_code: null, city: "", first_line: "", postal_code: "" },
      stripeVatError: null,
      isLoading: false,
      paddle: null,
      // Потолок ожидания загрузки js.paddle.com (см. ensurePaddle). Поле, а не константа модуля,
      // чтобы тест не ждал реальные секунды.
      paddleLoadTimeoutMs: 15000,
      error: null,
      countries,
      // автоплатёж (инлайн, recurrent тем же платежом)
      autopayConsent: false,
      rebill_type: "Sum",
      rebill_limit: 1000,
      rebill_sum: 1000,
      // FT-071: Stripe-автоплатёж (EUR) — состояние кнопок connect/detach
      stripeAutopayLoading: false,
      stripeAutopaySaving: false,
      // FT-071: EUR-порог/сумма Stripe-автосписания (отдельные поля от Tinkoff rebill_*).
      stripeRebillLimit: null,
      stripeRebillSum: null,
      // FT-147: рублёвая ветка — картой или по счёту T-Bank
      rubPaymentType: "card",
      tbankPayer: { name: "", inn: "", kpp: "", email: "" },
      tbankInvoice: null,
      tbankQrDataUrl: null,
      tbankQrFailed: false,
    };
  },
  computed: {
    main_company() {
      return this.$store.getters.getMainCompany || {};
    },
    company() {
      return (this.$store.getters.getCompany && this.$store.getters.getCompany.company) || {};
    },
    paymentProviderPreference() {
      return this.main_company.payment_provider_preference || "tinkoff";
    },
    isPaddlePayment() {
      return this.paymentProviderPreference === "paddle";
    },
    isTinkoffPayment() {
      return this.paymentProviderPreference === "tinkoff";
    },
    // Выбор ветки (зеркалит прежние взаимоисключающие табы): RUB+Tinkoff / Paddle / Euro.
    branch() {
      if (this.isTinkoffPayment && this.main_company.currency === "RUB") return "tinkoff";
      if (this.isPaddlePayment) return "paddle";
      return "euro";
    },
    // FT-147 (FM-05): клиентские правила реквизитов плательщика; бэкенд валидирует те же формы.
    // Срок оплаты приходит ISO-строкой (Date#iso8601) — показываем в формате леджера.
    tbankDueDateLabel() {
      const due = this.tbankInvoice && this.tbankInvoice.due_date;
      return due ? moment(due).format("DD.MM.YYYY") : "";
    },
    tbankPayerIsSoleProprietor() {
      return isSoleProprietor(this.tbankPayer.inn);
    },
    tbankInnRules() {
      return [(v) => isValidInn(v) || this.$t("transfers.tbank_inn_invalid")];
    },
    tbankKppRules() {
      // У ИП поле заблокировано и в payload не уходит — правило не должно красить его ошибкой.
      if (this.tbankPayerIsSoleProprietor) return [];
      return [(v) => isValidKpp(this.tbankPayer.inn, v) || this.$t("transfers.tbank_kpp_invalid")];
    },
    tbankEmailRules() {
      return [(v) => isValidEmail(v) || this.$t("transfers.tbank_email_invalid")];
    },
    isRuDomain() {
      return typeof window !== "undefined" && /\.ru$/i.test(window.location.hostname);
    },
    // Paddle-checkout (overlay) недоступен на .ru-домене → направляем на web.rentprog.com.
    paddleUnavailableOnRu() {
      return this.isRuDomain && (this.branch === "paddle" || (this.branch === "euro" && !this.isValidatedCompany));
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    planCurrency() {
      return (this.main_company.plan && this.main_company.plan.currency) || this.main_company.currency || "";
    },
    // Пол тарифа как его знает FE (фолбэк для payload без FT-150).
    planMinTopupSum() {
      return (this.main_company && this.main_company.plan && this.main_company.plan.min_topup_sum) || 0;
    },
    // FT-150 REQ-03: минимум ручного пополнения = пол (max(min_topup_sum, 3 × дневное списание)) +
    // модуль минуса — считает бэкенд (main_company.topup_minimum). Без поля — прежний пол тарифа.
    minTopupSum() {
      const mc = this.main_company || {};
      if (mc.topup_minimum !== undefined && mc.topup_minimum !== null) return Number(mc.topup_minimum) || 0;
      return this.planMinTopupSum;
    },
    // FT-150 REQ-05: нижняя граница суммы автоплатежа (пол, не выше 15 000 — main_company.topup_floor).
    rebillFloor() {
      const mc = this.main_company || {};
      if (mc.topup_floor !== undefined && mc.topup_floor !== null) return Number(mc.topup_floor) || 0;
      return this.planMinTopupSum;
    },
    // Развёрнутая подсказка нужна, когда минимум выше тарифного (есть минус или пол по парку).
    topupMinimumExplained() {
      return this.minTopupSum > this.planMinTopupSum;
    },
    // Дней списаний в поле: 3, для повторных блокировок 7 (payload topup_runway_days; старый payload — 3).
    topupRunwayDays() {
      const days = Number((this.main_company || {}).topup_runway_days);
      return days > 0 ? days : 3;
    },
    topupHint() {
      const params = { minimum: this.minTopupSum, min_topup_sum: this.minTopupSum, currency: this.planCurrency };
      if (!this.topupMinimumExplained) return this.$t("other.sum_warn_2", params);
      return this.topupRunwayDays > 3
        ? this.$t("transfers.topup_minimum_explain_repeat", params)
        : this.$t("transfers.topup_minimum_explain", params);
    },
    // Гейты кнопок оплаты: сумма > 0 и ≥ минимума (бэкенд-422 остаётся страховкой).
    sumValid() {
      return this.isSumValid(this.sum);
    },
    euroSumValid() {
      return this.isSumValid(this.euroPaymentSum);
    },
    isValidatedCompany() {
      return this.vatValidationResult && this.vatValidationResult.is_company === true;
    },
    // FT-071: автоплатёж Stripe подключён, если в main_company есть payment_method_id.
    isStripeAutopayConnected() {
      return !!(this.main_company && this.main_company.stripe_payment_method_id);
    },
    // FT-071: «лицо» привязанной карты — «Visa •••• 4242» (brand/last4 с бэка). null, если карту
    // привязали до захвата brand/last4 — тогда шаблон покажет только статус «подключён».
    stripeCardLabel() {
      const mc = this.main_company || {};
      const last4 = mc.stripe_card_last4;
      if (!last4) return null;
      const brand = mc.stripe_card_brand;
      const pretty = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "";
      return pretty ? `${pretty} •••• ${last4}` : `•••• ${last4}`;
    },
    // FT-071: порог/сумма валидны (гейтят «Подключить»/«Сохранить»). Зеркалит бэкенд
    // (FT-150 validate_rebill_sum): порог > 0, сумма ≥ rebillFloor и ≤ 15000.
    autopaySettingsValid() {
      const lim = parseFloat(this.stripeRebillLimit);
      const sum = parseFloat(this.stripeRebillSum);
      return lim > 0 && sum >= this.rebillFloor && sum <= 15000;
    },
    minInvoiceDueDate() {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      return date.toISOString().substr(0, 10);
    },
    maxInvoiceDueDate() {
      const date = new Date();
      date.setDate(date.getDate() + 30);
      return date.toISOString().substr(0, 10);
    },
    defaultInvoiceDueDate() {
      const date = new Date();
      date.setDate(date.getDate() + 14);
      return date.toISOString().substr(0, 10);
    },
    formattedInvoiceDueDate() {
      if (!this.invoiceDueDate) return "";
      const [year, month, day] = this.invoiceDueDate.split("-");
      return `${day}-${month}-${year}`;
    },
    formattedStripeInvoiceDueDate() {
      if (!this.stripeInvoiceDueDate) return "";
      const [year, month, day] = this.stripeInvoiceDueDate.split("-");
      return `${day}-${month}-${year}`;
    },
    sortedCountries() {
      const isRu = this.$i18n.locale === "ru";
      return this.countries
        .map((c) => ({ text: isRu ? (c.russian || c.name) : c.name, value: c.code }))
        .sort((a, b) => a.text.localeCompare(b.text, this.$i18n.locale));
    },
    countriesWithFlags() {
      const isRu = this.$i18n.locale === "ru";
      return this.countries
        .map((c) => ({ code: c.code, flag: c.flag, label: `${c.code} - ${isRu ? (c.russian || c.name) : c.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label, this.$i18n.locale));
    },
    vatPlaceholder() {
      const placeholders = {
        PT: "508123456", DE: "123456789", FR: "XX123456789", ES: "A12345678", IT: "12345678901",
        GB: "123456789", NL: "123456789B01", PL: "1234567890", RU: "1234567890", UA: "12345678",
        KZ: "123456789012", BY: "123456789",
      };
      return placeholders[this.vatCountryCode] || "123456789";
    },
    // Автоплатёж + правила суммы (зеркало FT-067).
    sumRules() {
      const min = this.minTopupSum;
      if (!min) return [];
      return [
        (v) => {
          const n = parseFloat(String(v == null ? "" : v).replace(/,/, "."));
          if (!n) return true;
          return n >= min || this.topupHint;
        },
      ];
    },
    rebillLimitRules() {
      return [
        (v) => (v !== "" && v !== null && v !== undefined) || this.$t("errors.required_field"),
        (v) => parseFloat(v) > 0 || this.$t("transfers.field_positive"),
      ];
    },
    rebillSumRules() {
      return [
        (v) => (v !== "" && v !== null && v !== undefined) || this.$t("errors.required_field"),
        (v) => parseFloat(v) >= this.rebillFloor || this.$t("transfers.rebill_min_hint", { sum: this.rebillFloor, currency: this.planCurrency }),
        (v) => parseFloat(v) <= 15000 || this.$t("transfers.rebill_max_hint", { sum: 15000, currency: this.planCurrency }),
      ];
    },
    autopayReady() {
      const limit = parseFloat(this.rebill_limit);
      const sum = parseFloat(this.rebill_sum);
      return limit > 0 && sum >= this.rebillFloor && sum <= 15000;
    },
  },
  watch: {
    // FT-150: минимум пересчитан бэкендом (cable/refresh) — подставляем в пустое/меньшее поле
    // (только при обновлении payload, не на ввод: правило поля должно уметь стать красным).
    minTopupSum() {
      if (this.value) this.prefillTopupSum();
    },
    value(v) {
      if (v) {
        this.onOpen();
        // Re-prefill по факту открытия: к первому переключению на «По счёту» стор
        // мог быть ещё не гидрирован (Vuex-ловушка проекта), пустые поля дозаполним.
        if (this.rubPaymentType === "invoice") this.tbankPrefillPayer();
      } else {
        this.tbankResetInvoice();
      }
    },
    rubPaymentType(v) {
      if (v === "invoice") this.tbankPrefillPayer();
    },

    paddlePaymentType(newVal) {
      if (newVal === "invoice") {
        if (!this.invoiceDueDate) this.invoiceDueDate = this.defaultInvoiceDueDate;
        this.prefillBillingAddress();
      }
    },
  },
  created() {
    this.handlePaddleReturn();
    this.handleStripeAutopayReturn();
  },
  beforeDestroy() {
    if (this._stripeReturnFocusHandler) {
      window.removeEventListener("focus", this._stripeReturnFocusHandler);
      this._stripeReturnFocusHandler = null;
    }
    if (this._stripeNativeReturnArmed) {
      Browser.removeAllListeners();
      this._stripeNativeReturnArmed = false;
    }
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.sum = 0;
      this.autopayConsent = false;
    },
    // Открытие диалога: гард по email + префилл (Stripe-адрес/VAT/due-date + автоплатёж).
    onOpen() {
      const hasEmail =
        (this.$store.getters.getCurrentUser && this.$store.getters.getCurrentUser.email) ||
        (this.company && this.company.email_for_bookings);
      if (!hasEmail) {
        this.$notify(this.$t("errors.no_email"), this.$t("errors.no_email_text"), "warning");
        this.$emit("input", false);
        return;
      }
      this.autopayConsent = false;
      this.prefillTopupSum();
      this.rebill_type = "Sum";
      this.rebill_sum = this.main_company.rebill_sum || this.defaultRebillSum();
      this.rebill_limit = this.main_company.rebill_limit || this.planMinTopupSum || 1000;
      // FT-071: EUR Stripe-автосписание — отдельные nullable-поля. NULL (не настроено) → EUR-подсказка
      // (не унаследованный рублёвый дефолт 500).
      this.stripeRebillSum = this.main_company.stripe_rebill_sum || this.defaultRebillSum();
      this.stripeRebillLimit = this.main_company.stripe_rebill_limit || this.defaultStripeRebillLimit();
      if (!this.stripeInvoiceDueDate) this.stripeInvoiceDueDate = this.defaultInvoiceDueDate;
      this.prefillStripeBillingAddress();
      // Прогрев Paddle SDK: до FT-070 его поднимала страница транзакций в created(), сейчас
      // владелец — этот диалог. На .ru-домене overlay всё равно недоступен — грузить нечего.
      if (this.branch === "paddle" && !this.paddleUnavailableOnRu) this.setupPaddle();
    },
    defaultRebillSum() {
      const plan = this.main_company.plan || {};
      let suggested = Math.round((plan.min_payment_sum || 0) * 30);
      suggested = Math.max(suggested, this.rebillFloor);
      suggested = Math.min(suggested, 15000);
      return suggested > 0 ? suggested : 1000;
    },
    // Порог по умолчанию: ~неделя использования (или min_topup) — небольшой буфер, заметно меньше суммы.
    defaultStripeRebillLimit() {
      const plan = this.main_company.plan || {};
      const weekly = Math.round((plan.min_payment_sum || 0) * 7);
      return Math.max(this.planMinTopupSum || 0, weekly) || 50;
    },
    onSumFocus() {
      if (this.sum == null || String(this.sum) === "0" || parseFloat(this.sum) === 0) this.sum = "";
    },
    onEuroSumFocus() {
      if (this.euroPaymentSum == null || String(this.euroPaymentSum) === "0" || parseFloat(this.euroPaymentSum) === 0) {
        this.euroPaymentSum = "";
      }
    },
    rebillDocUrl(path) {
      const locale = this.$root.$i18n.locale;
      const tld = locale === "ru" ? "ru" : "com";
      return `https://rentprog.${tld}/${locale}/${path}`;
    },

    // ─── RUB / Tinkoff ───
    // ─────────── FT-147: счёт T-Bank ───────────
    tbankPrefillPayer() {
      const defaults = defaultRequisites(this.company, this.$store.getters.getCurrentUser);
      Object.keys(defaults).forEach((k) => {
        if (!this.tbankPayer[k]) this.tbankPayer[k] = defaults[k];
      });
    },
    tbankResetInvoice() {
      this.tbankInvoice = null;
      this.tbankQrDataUrl = null;
      this.tbankQrFailed = false;
    },
    tbankCreateInvoice() {
      if (!this.isStaff) {
        this.$notify(this.$t("errors.no_access"), "", "warning");
        return;
      }
      const sum = parseFloat(String(this.sum).replace(/,/, "."));
      if (!sum || sum <= 0) {
        this.$notify(this.$t("errors.payment_sum"), this.$t("errors.payment_sum_text"), "warning", 5000);
        return;
      }
      if (sum < (this.minTopupSum || 0)) {
        this.$notify(this.$t("errors.payment_min", { min_topup_sum: this.minTopupSum, currency: this.planCurrency }), "", "warning", 5000);
        return;
      }
      const errorKey = requisitesErrorKey(this.tbankPayer);
      if (errorKey) return this.$notify(this.$t(errorKey), "", "warning");
      const payer = requisitesPayload(this.tbankPayer);

      this.isLoading = true;
      this.axios
        .post(
          "/api/v1/tbank_create_invoice",
          { invoice: { sum: sum.toFixed(2), payer } },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        )
        .then((response) => {
          this.tbankInvoice = { ...response.data, sum: sum.toFixed(2) };
          this.tbankRenderQr(response.data.qr_payload);
          this.$notify(this.$t("custom.success"), this.$t("transfers.tbank_invoice_created"), "success");
          this.$emit("paid");
        })
        .catch((error) => {
          const data = error.response && error.response.data;
          if (error.response && error.response.status === 409) {
            const key = data && data.error_code === "in_progress" ? "transfers.payment_processing" : "transfers.tbank_open_invoice_exists";
            const number = (data && data.invoice_number) || "";
            this.$notify(this.$t(key, { number }), "", "warning", 7000);
          } else if (!this.handleTopupError(error)) {
            this.setError(error, (data && data.error) || this.$t("errors.payment_creating"));
          }
          // Сбой выставления оставляет строку INIT_FAILED (FM-01) — леджер родителя должен её увидеть.
          if (data && data.transfer_id) this.$emit("paid");
        })
        .finally(() => (this.isLoading = false));
    },
    // Строка ST00012 приходит с бэка (реквизиты получателя, сумма копейками, назначение);
    // фронт только рисует QR. UTF-8 (третья группа заголовка "2") — кириллица допустима.
    // qrcode грузится отдельным чанком по факту выставления счёта: в главный
    // бандл (TopupDialog импортируется из App.vue) библиотеке делать нечего.
    // NB: отдельный чанк webpack не выделяет — qrcode статически импортируют StripePaymentDialog
    // и Store, библиотека и так в главном бандле; динамический import оставлен как безвредный.
    async tbankRenderQr(payload) {
      this.tbankQrFailed = false;
      if (!payload) {
        this.tbankQrDataUrl = null;
        this.tbankQrFailed = true;
        return;
      }
      try {
        const { default: QRCode } = await import(/* webpackChunkName: "qrcode" */ "qrcode");
        this.tbankQrDataUrl = await QRCode.toDataURL(payload, { width: 220, margin: 1, errorCorrectionLevel: "M" });
      } catch (err) {
        console.error("T-Bank invoice QR generation error:", err);
        this.tbankQrDataUrl = null;
        this.tbankQrFailed = true; // PDF-ссылка остаётся — оплата платёжкой по счёту доступна
      }
    },

    transactionCreate() {
      if (this.isStaff) {
        if (this.sum && parseFloat(this.sum.replace(/,/, ".")) && parseFloat(this.sum.replace(/,/, ".")) > 0) {
          if (parseFloat(String(this.sum).replace(/,/, ".")) >= this.minTopupSum) {
            this.isLoading = true;
            this.axios
              .post(
                "/api/v1/create_transfer",
                {
                  transfers: {
                    sum: parseFloat(this.sum.replace(/,/, ".")).toFixed(2),
                    recurrent: this.autopayConsent,
                    turn_off: false,
                    promo_code: this.promo_code,
                    ...(this.autopayConsent ? {
                      rebill_type: this.rebill_type,
                      rebill_sum: parseFloat(this.rebill_sum),
                      rebill_limit: parseFloat(this.rebill_limit),
                    } : {}),
                  },
                },
                { headers: { Authorization: this.$store.getters.getAuthToken } }
              )
              .then((response) => {
                if (response.data.Success == true && response.data.PaymentURL) {
                  window.location.replace(response.data.PaymentURL);
                  this.$emit("input", false);
                  this.sum = 0;
                } else {
                  this.$notify(response.data.Message, response.data.Details, "error");
                }
              })
              .catch((error) => {
                this.isLoading = false;
                if (error.response && error.response?.status && error.response?.status == 406) {
                  this.$notify(this.$t("errors.promocode_not_found_title"), this.$t("errors.promocode_not_found_text"), "error");
                  this.promo_code = null;
                } else if (!this.handleTopupError(error)) {
                  this.setError(error, this.$t("errors.payment_creating"));
                }
              })
              .finally(() => (this.isLoading = false));
          } else {
            this.$notify(this.$t("errors.payment_min", { min_topup_sum: this.minTopupSum, currency: this.planCurrency }), "warning", 5000);
          }
        } else {
          this.$notify(this.$t("errors.payment_sum"), this.$t("errors.payment_sum_text"), "warning", 5000);
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },

    // ─── Paddle ───
    // SDK поднимаем здесь же, перед каждой оплатой: onOpen греет его заранее, но полагаться на
    // прогрев нельзя — main_company приезжает из Vuex асинхронно, и на момент открытия диалога
    // branch мог быть ещё не paddle (регресс FT-070: после выноса диалога инициализация в
    // paddle-ветке пропала совсем, и «обновите страницу» ничего не чинило). Вызов не пропускаем
    // и при уже поднятом SDK — он возвращает владение eventCallback этому диалогу (см. setupPaddle).
    async paddleCreateTransaction() {
      if (this.isStaff) {
        if (this.sum && parseFloat(this.sum.replace(/,/, ".")) && parseFloat(this.sum.replace(/,/, ".")) > 0) {
          if (parseFloat(String(this.sum).replace(/,/, ".")) >= this.minTopupSum) {
            this.isLoading = true;
            try {
              await this.ensurePaddle();
            } finally {
              this.isLoading = false;
            }
            if (!this.paddle) {
              // Сюда доходим только при реальном отказе загрузки js.paddle.com (блокировщик, сеть).
              this.$notify(this.$t("errors.error"), this.$t("errors.paddle_not_loaded"), "warning");
              return;
            }
            this.isLoading = true;
            const self = this;
            this.axios
              .post(
                "/api/v1/paddle_create_transaction",
                { transfers: { sum: parseFloat(this.sum.replace(/,/, ".")).toFixed(2), promo_code: this.promo_code } },
                { headers: { Authorization: this.$store.getters.getAuthToken } }
              )
              .then((response) => {
                if (response.data.transaction_id && self.paddle) {
                  self.paddle.Checkout.open({
                    transactionId: response.data.transaction_id,
                    settings: { displayMode: "overlay", theme: self.$vuetify.theme.dark ? "dark" : "light", locale: self.$root.$i18n.locale },
                  });
                  this.isLoading = false;
                } else {
                  this.isLoading = false;
                  this.$notify(this.$t("errors.error"), this.$t("errors.payment_creating"), "error");
                }
              })
              .catch((error) => {
                this.isLoading = false;
                if (error.response && error.response?.status && error.response?.status == 406) {
                  this.$notify(this.$t("errors.promocode_not_found_title"), this.$t("errors.promocode_not_found_text"), "error");
                  this.promo_code = null;
                } else if (!this.handleTopupError(error)) {
                  this.setError(error, this.$t("errors.payment_creating"));
                }
              });
          } else {
            this.$notify(this.$t("errors.payment_min", { min_topup_sum: this.minTopupSum, currency: this.planCurrency }), "", "warning");
          }
        } else {
          this.$notify(this.$t("errors.payment_sum"), this.$t("errors.payment_sum_text"), "warning");
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    paddleCreateInvoice() {
      if (this.isStaff) {
        if (!this.billingAddress.country_code || !this.billingAddress.city || !this.billingAddress.first_line || !this.billingAddress.postal_code) {
          this.$notify(this.$t("errors.fill_billing_address"), "", "warning");
          return;
        }
        if (this.sum && parseFloat(this.sum.replace(/,/, ".")) && parseFloat(this.sum.replace(/,/, ".")) > 0) {
          if (parseFloat(String(this.sum).replace(/,/, ".")) >= this.minTopupSum) {
            this.isLoading = true;
            this.axios
              .post(
                "/api/v1/paddle_create_invoice",
                {
                  transfers: { sum: parseFloat(this.sum.replace(/,/, ".")).toFixed(2) },
                  due_date: this.invoiceDueDate,
                  billing_address: {
                    country_code: this.billingAddress.country_code,
                    city: this.billingAddress.city,
                    first_line: this.billingAddress.first_line,
                    postal_code: this.billingAddress.postal_code,
                  },
                },
                { headers: { Authorization: this.$store.getters.getAuthToken } }
              )
              .then((response) => {
                this.isLoading = false;
                this.$emit("input", false);
                const companyData = this.$store.getters.getCompany;
                if (companyData && companyData.company) {
                  companyData.company.billing_address = { ...this.billingAddress };
                  this.$store.commit("setCompany", companyData);
                }
                this.sum = 0;
                this.paddlePaymentType = "transaction";
                this.invoiceDueDate = null;
                this.resetBillingAddress();
                this.$notify(this.$t("custom.success"), this.$t("transfers.invoice_created"), "success");
                if (response.data.invoice_url) window.open(response.data.invoice_url, "_blank");
                this.$emit("paid");
              })
              .catch((error) => {
                this.isLoading = false;
                if (!this.handleTopupError(error)) this.setError(error, this.$t("errors.payment_creating"));
              });
          } else {
            this.$notify(this.$t("errors.payment_min", { min_topup_sum: this.minTopupSum, currency: this.planCurrency }), "", "warning");
          }
        } else {
          this.$notify(this.$t("errors.payment_sum"), this.$t("errors.payment_sum_text"), "warning");
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    // Euro-Paddle (физлицо без VAT). Фикс пред-сущ. бага: paddleCreateTransaction читает
    // this.sum, поэтому переносим euroPaymentSum в sum (строкой) перед вызовом.
    // Своей инициализации SDK здесь нет — её делает paddleCreateTransaction (один канон).
    paddlePayWithEuro() {
      this.sum = String(this.euroPaymentSum);
      return this.paddleCreateTransaction();
    },

    // ─── Euro / Stripe-автоплатёж (hosted Checkout) ───
    // Подключение: бэкенд возвращает hosted Checkout url. Открываем его в НОВОЙ вкладке, чтобы не
    // выкидывать пользователя из приложения. Вкладку открываем СИНХРОННО в обработчике клика (иначе
    // попап-блокер зарежет window.open из async .then) — пустую, а URL подставляем по ответу бэкенда.
    // success_url/cancel_url — страница приложения с маркером для возврата (отрабатывает в новой вкладке).
    connectStripeAutopay() {
      if (!this.requireStaff()) return;
      if (!this.autopaySettingsValid) {
        this.$notify(this.$t("errors.error"), this.$t("transfers.field_positive"), "warning");
        return;
      }
      const isNativeApp = Capacitor.isNativePlatform();
      // Веб: СИНХРОННО открываем пустую вкладку (иначе попап-блокер зарежет window.open из async .then).
      // Натив: попап-блокера нет — Checkout откроем через @capacitor/browser ПОСЛЕ ответа бэкенда.
      const checkoutWindow = isNativeApp ? null : window.open("", "_blank");
      // FT-071 deep-return: на нативе success_url ведём на universal link web.rentprog.com/app-return
      // (iOS AASA исключает /transactions из universal links → выделенный /app-return). На вебе —
      // текущая страница (та же вкладка, handleStripeAutopayReturn).
      const base = isNativeApp
        ? "https://web.rentprog.com/app-return"
        : window.location.origin + window.location.pathname;
      const headers = { Authorization: this.$store.getters.getAuthToken };
      this.stripeAutopayLoading = true;
      // Сначала сохраняем порог/сумму, затем создаём Checkout-сессию.
      this.axios
        .post("/api/v1/stripe_autopay_settings", { rebill_limit: this.stripeRebillLimit, rebill_sum: this.stripeRebillSum }, { headers })
        .then(() =>
          this.axios.post(
            "/api/v1/stripe_autopay_checkout",
            { success_url: `${base}?stripe_autopay=success`, cancel_url: `${base}?stripe_autopay=cancel` },
            { headers }
          )
        )
        .then((response) => {
          this.stripeAutopayLoading = false;
          const url = response.data && response.data.url;
          if (!url) {
            if (checkoutWindow) checkoutWindow.close();
            this.$notify(this.$t("errors.error"), this.$t("errors.payment_creating"), "error");
            return;
          }
          if (isNativeApp) {
            // Натив: @capacitor/browser (iOS SFSafariViewController / Android Custom Tabs). Возврат —
            // по закрытию браузера (browserFinished); на Android App Link дополнительно дёрнет appUrlOpen.
            // На iOS universal link ВНУТРИ SFSafariViewController подавлен → browserFinished обязателен.
            this.armStripeNativeReturnRefresh();
            Browser.open({ url });
          } else if (checkoutWindow) {
            checkoutWindow.location.href = url;
            this.armStripeReturnRefresh();
          } else {
            // Попап заблокирован (web) → фолбэк: редирект в текущей вкладке.
            window.location.href = url;
          }
        })
        .catch((error) => {
          this.stripeAutopayLoading = false;
          if (checkoutWindow) checkoutWindow.close();
          if (!this.handleTopupError(error)) this.setError(error, this.$t("errors.payment_creating"));
        });
    },
    // Сохранение порога/суммы автосписания в подключённом состоянии — отдельным запросом.
    saveStripeAutopaySettings() {
      if (!this.autopaySettingsValid) {
        this.$notify(this.$t("errors.error"), this.$t("transfers.field_positive"), "warning");
        return;
      }
      this.stripeAutopaySaving = true;
      this.axios
        .post(
          "/api/v1/stripe_autopay_settings",
          { rebill_limit: this.stripeRebillLimit, rebill_sum: this.stripeRebillSum },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        )
        .then(() => {
          this.refreshMainCompany();
          this.$notify(this.$t("custom.success"), this.$t("custom.saved"), "success");
        })
        .catch((error) => { if (!this.handleTopupError(error)) this.setError(error, this.$t("errors.error")); })
        .finally(() => (this.stripeAutopaySaving = false));
    },
    // Привязка идёт в новой вкладке; PM сохраняется вебхуком checkout.session.completed на бэке.
    // Когда пользователь вернётся на ЭТУ вкладку (focus) — один раз перечитываем main_company, чтобы
    // панель показала «подключено» без перезагрузки. Если вебхук ещё не дошёл — один отложенный
    // повтор. Refresh безопасен и при отмене (PM не появится → тихий no-op без нотификации).
    armStripeReturnRefresh() {
      if (this._stripeReturnFocusHandler) return;
      const tryRefresh = (retriesLeft) => {
        const p = this.refreshMainCompany();
        if (!p) return;
        p.then(() => {
          if (this.isStripeAutopayConnected) {
            this.$notify(this.$t("custom.success"), this.$t("transfers.stripe_autopay_connected_notice"), "success");
          } else if (retriesLeft > 0) {
            setTimeout(() => tryRefresh(retriesLeft - 1), 2500);
          }
        });
      };
      this._stripeReturnFocusHandler = () => {
        window.removeEventListener("focus", this._stripeReturnFocusHandler);
        this._stripeReturnFocusHandler = null;
        tryRefresh(1);
      };
      window.addEventListener("focus", this._stripeReturnFocusHandler);
    },
    // FT-071 натив: возврат из Checkout по закрытию @capacitor/browser (browserFinished) → один
    // refresh main_company (карта уже привязана вебхуком checkout.session.completed). Идемпотентно с
    // appUrlOpen (App Link на Android). Слушатель снимаем после первого срабатывания.
    armStripeNativeReturnRefresh() {
      if (this._stripeNativeReturnArmed) return;
      this._stripeNativeReturnArmed = true;
      Browser.addListener("browserFinished", () => {
        this._stripeNativeReturnArmed = false;
        Browser.removeAllListeners();
        const p = this.refreshMainCompany();
        if (!p) return;
        p.then(() => {
          if (this.isStripeAutopayConnected) {
            this.$notify(this.$t("custom.success"), this.$t("transfers.stripe_autopay_connected_notice"), "success");
          }
        });
      });
    },
    // Отвязка карты — бэкенд чистит stripe_payment_method_id; обновляем main_company.
    detachStripeAutopay() {
      if (this.isStaff) {
        this.stripeAutopayLoading = true;
        this.axios
          .post("/api/v1/stripe_autopay_detach", {}, { headers: { Authorization: this.$store.getters.getAuthToken } })
          .then(() => {
            this.refreshMainCompany();
            this.$notify(this.$t("custom.success"), this.$t("transfers.stripe_autopay_detached"), "success");
          })
          .catch((error) => this.setError(error, this.$t("errors.error")))
          .finally(() => (this.stripeAutopayLoading = false));
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    // Возврат из hosted Checkout (?stripe_autopay=success|cancel) — один раз глобально
    // (module-флаг), т.к. компонент смонтирован и в App.vue, и на /transactions.
    handleStripeAutopayReturn() {
      if (stripeAutopayReturnHandled) return;
      const status = new URLSearchParams(window.location.search).get("stripe_autopay");
      if (!status) return;
      stripeAutopayReturnHandled = true;
      window.history.replaceState({}, document.title, window.location.pathname);
      if (status === "success") {
        this.refreshMainCompany();
        this.$notify(this.$t("custom.success"), this.$t("transfers.stripe_autopay_connected_notice"), "success");
      }
      // cancel → тихо вычистили query-параметр выше.
    },

    // ─── Euro / Stripe Invoice (юрлицо с валидным VAT) ───
    stripeCreateInvoice() {
      if (this.isStaff) {
        if (!this.isValidatedCompany || !this.stripeVatNumber) {
          this.$notify(this.$t("errors.error"), this.$t("tariffs.vat_required_for_invoice"), "warning");
          return;
        }
        if (!this.stripeBillingAddress.country_code || !this.stripeBillingAddress.city || !this.stripeBillingAddress.first_line || !this.stripeBillingAddress.postal_code) {
          this.$notify(this.$t("errors.fill_billing_address"), "", "warning");
          return;
        }
        if (this.euroPaymentSum && parseFloat(this.euroPaymentSum.toString().replace(/,/, ".")) && parseFloat(this.euroPaymentSum.toString().replace(/,/, ".")) > 0) {
          if (this.isSumValid(this.euroPaymentSum)) {
            this.isLoading = true;
            const dueDays = this.stripeInvoiceDueDate
              ? Math.ceil((new Date(this.stripeInvoiceDueDate) - new Date()) / (1000 * 60 * 60 * 24))
              : 14;
            this.axios
              .post(
                "/api/v1/stripe_create_invoice",
                {
                  transfers: { sum: parseFloat(this.euroPaymentSum.toString().replace(/,/, ".")).toFixed(2) },
                  vat_number: this.stripeVatNumber,
                  due_days: dueDays,
                  billing_address: {
                    country_code: this.stripeBillingAddress.country_code,
                    city: this.stripeBillingAddress.city,
                    first_line: this.stripeBillingAddress.first_line,
                    postal_code: this.stripeBillingAddress.postal_code,
                  },
                },
                {
                  headers: { Authorization: this.$store.getters.getAuthToken },
                  // Дефолтный axios-таймаут 30 с (main.js) меньше, чем сервер тратит
                  // в худшем случае: там 7-8 последовательных вызовов Stripe и потолок
                  // Rack::Timeout 180 с. Обрыв на 30-й секунде показывал ошибку, хотя
                  // счёт уже уходил клиенту, и провоцировал повтор.
                  // Ставим ВЫШЕ серверного потолка, а не «с запасом к p95»: любой
                  // порог ниже 180 с означает, что при медленном Stripe мы выбросим
                  // успешный ответ вместе с invoice_url и напугаем пользователя там,
                  // где сервер отработал штатно. p95 эндпоинта ~5 с, так что ждать
                  // три минуты придётся только в реально аварийном случае, и всё это
                  // время кнопка недоступна.
                  timeout: 185000,
                }
              )
              .then((response) => {
                // Тело в try/catch: при форме .then(A).catch(B) ошибка из A уезжает
                // в B, и пользователь при успешном 200 видел сообщение о таймауте.
                try {
                this.isLoading = false;
                this.$emit("input", false);
                const companyData = this.$store.getters.getCompany;
                if (companyData && companyData.company) {
                  companyData.company.billing_address = { ...this.stripeBillingAddress };
                  this.$store.commit("setCompany", companyData);
                }
                const mainCompanyData = this.$store.getters.getMainCompany;
                if (mainCompanyData) {
                  mainCompanyData.vat_number = this.stripeVatNumber;
                  this.$store.commit("setMainCompany", mainCompanyData);
                }
                this.euroPaymentSum = 0;
                this.stripeInvoiceDueDate = null;
                if (response.data.already_exists) {
                  // Сервер отдал ПЕРВЫЙ счёт вместо создания второго.
                  this.$notify(this.$t("errors.attention"), this.$t("transfers.invoice_already_exists"), "info", 10000);
                } else {
                  this.$notify(this.$t("custom.success"), this.$t("transfers.invoice_created"), "success");
                }
                if (response.data.invoice_url) window.open(response.data.invoice_url, "_blank");
                this.$emit("paid");
                } catch (e) {
                  // Форма .then(A).catch(B) отправляет ошибку из A в B, и успешный
                  // 200 показывался как «сервер не ответил вовремя». Здесь ловим
                  // сами: счёт создан, сообщать надо об этом.
                  this.isLoading = false;
                  this.$notify(this.$t("custom.success"), this.$t("transfers.invoice_created"), "success");
                }
              })
              .catch((error) => {
                this.isLoading = false;
                // Ответ не дошёл (таймаут/обрыв связи): счёт мог быть создан на сервере.
                // Отправлять пользователя на повтор вслепую нельзя — пусть сперва
                // посмотрит список счетов.
                // Таймаут: запрос ушёл, ответа нет — счёт мог быть создан. Держим
                // сообщение на экране заметно дольше дефолтных 3 с: это ровно тот
                // совет, ради которого весь фикс и делался.
                if (error.code === "ECONNABORTED") {
                  this.$notify(this.$t("errors.attention"), this.$t("transfers.invoice_maybe_created"), "warning", 15000);
                  this.$emit("paid");
                  return;
                }
                // Сеть/CORS: запрос, скорее всего, не ушёл вовсе. Пугать «счёт мог
                // быть создан» незачем, глобальный сетевой баннер уже показан.
                if (!error.response) {
                  this.setError(error, this.$t("errors.payment_creating"));
                  return;
                }
                // 409 — сервер сам отсёк дубль, это не ошибка пользователя.
                if (error.response.status === 409) {
                  // details_changed: есть живой неоплаченный счёт, а реквизиты
                  // прислали другие. Ждать бесполезно — надо закрыть первый счёт,
                  // поэтому и подача другая, и на экране дольше.
                  const detailsChanged = error.response?.data?.details_changed === true;
                  this.$notify(
                    this.$t("errors.attention"),
                    error.response?.data?.error ||
                      (detailsChanged
                        ? this.$t("transfers.invoice_open_other_details")
                        : this.$t("transfers.invoice_maybe_created")),
                    "warning",
                    detailsChanged ? 15000 : 10000
                  );
                  this.$emit("paid");
                  return;
                }
                if (this.handleTopupError(error)) return;
                if (error.response?.data && error.response?.data.error) {
                  this.$notify(this.$t("errors.error"), error.response?.data.error, "error");
                } else {
                  this.setError(error, this.$t("errors.payment_creating"));
                }
              });
          } else {
            this.$notify(this.$t("errors.payment_min", { min_topup_sum: this.minTopupSum, currency: this.planCurrency || "EUR" }), "", "warning");
          }
        } else {
          this.$notify(this.$t("errors.payment_sum"), this.$t("errors.payment_sum_text"), "warning");
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },

    // ─── VAT ───
    onVatCountryChange() {
      if (this.vatCountryCode) this.stripeBillingAddress.country_code = this.vatCountryCode;
      if (this.stripeVatNumber && this.stripeVatNumber.trim().length >= 5) this.validateVatAsync();
    },
    onVatInput() {
      if (this.vatValidationDebounce) {
        clearTimeout(this.vatValidationDebounce);
        this.vatValidationDebounce = null;
      }
      if (!this.stripeVatNumber || this.stripeVatNumber.trim().length < 5) {
        this.vatValidationResult = null;
        this.stripeVatError = null;
        this.vatValidationLoading = false;
        return;
      }
      if (this.vatCountryCode && this.stripeVatNumber.trim().length >= 5) this.validateVatAsync();
    },
    validateVatAsync() {
      if (this.vatValidationDebounce) clearTimeout(this.vatValidationDebounce);
      this.vatValidationDebounce = setTimeout(async () => {
        const vatNumber = this.stripeVatNumber && this.stripeVatNumber.trim().toUpperCase();
        const countryCode = this.vatCountryCode;
        if (!countryCode || !vatNumber || vatNumber.length < 5) {
          this.vatValidationResult = null;
          return;
        }
        this.vatValidationLoading = true;
        try {
          const response = await this.axios.post(
            "/api/v1/validate_vat",
            { country_code: countryCode, vat_number: vatNumber },
            { headers: { Authorization: this.$store.getters.getAuthToken } }
          );
          this.vatValidationResult = response.data;
        } catch (error) {
          console.error("VAT validation error:", error);
          this.vatValidationResult = null;
        } finally {
          this.vatValidationLoading = false;
        }
      }, 500);
    },

    // ─── billing-address префилл ───
    resetBillingAddress() {
      this.billingAddress = { country_code: null, city: "", first_line: "", postal_code: "" };
    },
    prefillBillingAddress() {
      const savedAddress = this.company && this.company.billing_address;
      if (savedAddress && Object.keys(savedAddress).length > 0) {
        this.billingAddress = {
          country_code: savedAddress.country_code || null,
          city: savedAddress.city || "",
          first_line: savedAddress.first_line || "",
          postal_code: savedAddress.postal_code || "",
        };
      }
    },
    prefillStripeBillingAddress() {
      const savedAddress = this.company && this.company.billing_address;
      if (savedAddress && Object.keys(savedAddress).length > 0) {
        this.stripeBillingAddress = {
          country_code: savedAddress.country_code || null,
          city: savedAddress.city || "",
          first_line: savedAddress.first_line || "",
          postal_code: savedAddress.postal_code || "",
        };
        if (savedAddress.country_code) this.vatCountryCode = savedAddress.country_code;
      }
      const savedVatNumber = this.main_company && this.main_company.vat_number;
      if (savedVatNumber) {
        this.stripeVatNumber = savedVatNumber;
        if (this.vatCountryCode) this.$nextTick(() => this.validateVatAsync());
      }
    },

    // ─── Paddle SDK ───
    // Раннего выхода «уже поднят» здесь нет намеренно. initializePaddle идемпотентен по загрузке
    // (промис скрипта кеширован в модуле библиотеки), но eventCallback у Paddle ОДИН на страницу:
    // при повторном вызове библиотека делает Paddle.Update(...) и переписывает его. Диалог же
    // смонтирован дважды (App.vue и Transactions.vue), поэтому checkout.completed достаётся тому
    // экземпляру, который инициализировал последним. Вызов перед оплатой возвращает владение
    // колбэком активному диалогу — иначе платёж пройдёт, а закроется и обновит баланс чужая копия.
    async setupPaddle() {
      try {
        let paddleToken = "live_f4ca880254002f43f1ed122da05";
        if (process.env.NODE_ENV === "development") paddleToken = "test_2656d9d7ee7236e5a38dd97df6a";
        this.paddle = await initializePaddle({
          token: paddleToken,
          eventCallback: (data) => {
            if (data.name && (data.name.includes("error") || data.name.includes("failed"))) {
              console.error("Paddle Error:", data.name, data.detail || data.message);
            }
            if (data.name === "checkout.completed") {
              this.$emit("input", false);
              this.sum = 0;
              this.promo_code = null;
              this.$notify(this.$t("custom.success"), this.$t("transfers.payment_processing"), "success");
              setTimeout(() => {
                this.refreshMainCompany();
                this.$emit("paid");
              }, 5000);
            }
          },
        });
        if (paddleToken.startsWith("test_") && this.paddle && this.paddle.Environment) {
          this.paddle.Environment.set("sandbox");
        }
      } catch (error) {
        // Уведомляет вызывающий — он знает, нажал ли пользователь «Оплатить» или это фоновый
        // прогрев. Иначе на один отказ загрузки приходило два уведомления подряд.
        console.error("Failed to initialize Paddle:", error);
      }
    },
    // Загрузка SDK с потолком ожидания. Промис initializePaddle завершается только по load/error
    // скрипта: при зависшем (не оборванном) соединении он не завершается никогда, и кнопка оплаты
    // осталась бы в спиннере навсегда. По таймауту идём в ветку «SDK не поднялся».
    async ensurePaddle() {
      let timer = null;
      try {
        await Promise.race([
          this.setupPaddle(),
          new Promise((resolve) => {
            timer = setTimeout(resolve, this.paddleLoadTimeoutMs);
          }),
        ]);
      } finally {
        if (timer) clearTimeout(timer);
      }
      return this.paddle;
    },
    // Возврат с Paddle (?_ptxn=...) — открыть checkout. Один раз глобально (module-флаг),
    // т.к. компонент смонтирован и в App.vue, и на /transactions.
    async handlePaddleReturn() {
      if (ptxnHandled) return;
      const transactionId = new URLSearchParams(window.location.search).get("_ptxn");
      if (!transactionId) return;
      ptxnHandled = true;
      window.history.replaceState({}, document.title, window.location.pathname);
      await this.ensurePaddle();
      if (!this.paddle) {
        // Пользователь вернулся с платёжной страницы — молчать здесь нельзя, иначе checkout
        // просто не откроется без объяснения.
        this.$notify(this.$t("errors.error"), this.$t("errors.paddle_not_loaded"), "warning");
        return;
      }
      try {
        this.paddle.Checkout.open({
          transactionId,
          settings: { displayMode: "overlay", theme: this.$vuetify.theme.dark ? "dark" : "light", locale: this.$root.$i18n.locale },
        });
      } catch (checkoutError) {
        console.error("Paddle checkout error:", checkoutError);
        this.$notify(this.$t("custom.info"), this.$t("transfers.invoice_checkout_not_available"), "info");
      }
    },
    // Сумма валидна для кнопки: число с не более чем двумя знаками (запятая = точка), > 0, ≥ минимума.
    // Тот же критерий, что у бэкенда (TOPUP_SUM_FORMAT) — «1350abc» и «1e3» кнопку не включают.
    isSumValid(raw) {
      const str = String(raw == null ? "" : raw).trim().replace(",", ".");
      if (!/^\d+(\.\d{1,2})?$/.test(str)) return false;
      const n = parseFloat(str);
      return n > 0 && n >= this.minTopupSum;
    },
    // FT-150 REQ-03: минимум как сумма по умолчанию, если поле пустое или меньше минимума.
    prefillTopupSum() {
      const min = this.minTopupSum;
      if (!min) return;
      const current = parseFloat(String(this.sum == null ? "" : this.sum).replace(/,/, "."));
      if (!current || current < min) this.sum = String(min);
      const euro = parseFloat(String(this.euroPaymentSum == null ? "" : this.euroPaymentSum).replace(/,/, "."));
      if (!euro || euro < min) this.euroPaymentSum = String(min);
    },
    // FT-150 CTR-02/CTR-04: 422 минимума/пола — показать текст бэкенда (самодостаточный, с суммой)
    // и перечитать main_company, чтобы подсказка и дефолт обновились. true — ошибка обработана.
    handleTopupError(error) {
      const data = error && error.response && error.response.data;
      const code = data && data.error_code;
      if (!["topup_below_minimum", "topup_invalid_sum", "rebill_sum_below_floor", "invalid_rebill_sum"].includes(code)) return false;
      this.setError(error, this.$t("errors.payment_creating")); // текст бэкенда — detail миксина
      this.refreshMainCompany();
      return true;
    },
    refreshMainCompany() {
      const cid = (this.company && this.company.main_company_id) || (this.main_company && this.main_company.id);
      if (!cid) return;
      return this.axios
        .get(`api/v1/main_companies/${cid}`, { headers: { Authorization: this.$store.getters.getAuthToken } })
        .then((r) => this.$store.commit("setMainCompany", r.data))
        .catch((e) => console.error("Failed to refresh balance:", e));
    },

  },
};
</script>

<style lang="less">
/* FT-070: единый диалог пополнения. Refined fintech, theme-agnostic (нейтральный rgba +
   Vuetify success). Namespace .topup-dialog (диалог рендерится в body). */
.topup-dialog {
  border-radius: 18px !important;
  overflow: hidden;

  .td-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 18px 6px 24px;

    &__icon {
      color: #43a047 !important;
      background: rgba(67, 160, 71, 0.12);
      border-radius: 12px;
      padding: 8px;
      font-size: 22px !important;
    }
    &__title {
      font-size: 1.2rem;
      font-weight: 700;
      letter-spacing: -0.01em;
    }
  }

  .td-body {
    padding: 10px 26px 18px !important;
  }

  .td-amount__field {
    input {
      font-size: 1.7rem;
      font-weight: 700;
      letter-spacing: -0.01em;
      padding-bottom: 4px;
    }
    .v-text-field__suffix {
      font-size: 1rem;
      font-weight: 600;
      opacity: 0.5;
      align-self: flex-end;
      padding-bottom: 8px;
    }
    .v-label { font-size: 0.8rem; letter-spacing: 0.02em; }
  }

  .td-promo { margin-top: 12px; }

  .td-foot {
    font-size: 0.72rem;
    opacity: 0.55;
    margin-top: 14px;
    line-height: 1.4;
    p { margin-bottom: 4px; }
  }

  .td-info-alert { font-size: 0.8rem; }

  .td-autopay {
    margin-top: 18px;
    border: 1px solid rgba(128, 128, 128, 0.22);
    border-radius: 14px;
    background: rgba(128, 128, 128, 0.045);
    overflow: hidden;
    transition: border-color 0.25s ease, background 0.25s ease;

    &--on {
      border-color: rgba(67, 160, 71, 0.55);
      background: rgba(67, 160, 71, 0.07);
    }
    &__panel,
    .v-expansion-panel,
    .v-expansion-panel::before {
      background: transparent !important;
      box-shadow: none !important;
    }
    &__header { padding: 14px 16px; min-height: 52px !important; }
    &__icon { margin-right: 10px; }
    &__title { font-size: 0.9rem; font-weight: 600; letter-spacing: 0.01em; }
    &__content .v-expansion-panel-content__wrap { padding: 0 16px 16px; }
    &__fields { display: flex; gap: 14px; margin-top: 4px; }

    /* FT-071: статичный Stripe-автоплатёж — отдельная карточка-секция внизу EUR-формы */
    &--card { margin-top: 0; padding: 14px 16px; }
    &__head {
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }
    &__body { flex: 1; min-width: 0; }
    &__sub {
      font-size: 0.74rem;
      opacity: 0.7;
      line-height: 1.4;
      margin-top: 2px;
    }
    &__check { margin-top: 1px; }
    &__card {
      font-size: 0.84rem;
      font-weight: 600;
      letter-spacing: 0.03em;
      margin-top: 3px;
      display: flex;
      align-items: center;
    }
    &__card-icon { margin-right: 5px; opacity: 0.65; }
    &__btn { text-transform: none; font-weight: 600; }
    &__btn--connect { margin-top: 14px; letter-spacing: 0.01em; }
    &__settings {
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px dashed rgba(128, 128, 128, 0.2);
    }
    /* FT-071: футер настроек — Save слева, Disconnect справа (один ряд действий) */
    &__footer {
      display: flex;
      align-items: center;
      margin-top: 10px;
    }
  }

  .td-autopay-section { margin-top: 20px; }
  .td-divider {
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(128, 128, 128, 0.28), transparent);
    margin-bottom: 16px;
  }

  .td-notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.78rem;
    line-height: 1.45;
    opacity: 0.82;
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(128, 128, 128, 0.07);
    &__icon { opacity: 0.6; margin-top: 1px; }
  }

  .td-links {
    margin-top: 12px;
    font-size: 0.74rem;
    a {
      text-decoration: none;
      font-weight: 500;
      opacity: 0.85;
      &:hover { opacity: 1; text-decoration: underline; }
    }
    &__sep { opacity: 0.35; margin: 0 8px; }
  }

  .td-consent {
    margin-top: 10px;
    .v-label { font-size: 0.76rem; line-height: 1.4; opacity: 0.85; }
  }

  .td-pay {
    margin-top: 22px;
    border-radius: 12px !important;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: none;
    height: 48px !important;
    box-shadow: 0 6px 18px -8px rgba(67, 160, 71, 0.55);
  }

  .td-actions { padding: 4px 18px 14px; }
  .td-close { text-transform: none; opacity: 0.7; &:hover { opacity: 1; } }
}
/* FT-147: QR платёжки в результате выставления счёта */
.td-qr { width: 220px; height: 220px; display: block; margin: 0 auto; }
.td-tbank-result { padding: 4px 0; }
</style>
